import { environmental_products as adminMarketPriceTableDefinitions } from "../openapi-typescript/admin/models/environmental_products";
import { environmental_products as approvedMarketPriceTableDefinitions } from "../openapi-typescript/approved/models/environmental_products";
import { startTransition, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDeepCompareEffect, useHash } from "react-use";
import { NumberFormat } from "./globals";
import DOMPurify, { Config } from "dompurify";
import { CORETableColumn } from "../COREDesignSystem/Content/CORETable";

type YearNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type MagnitudeTableEmission = "t" | "kt" | "Mt" | "Gt" | "auto";
export type Year = `20${YearNumber}${YearNumber}`;
export type SpacingSizes = {
  xxs: number;
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
};
export type SpacingName = keyof SpacingSizes;
export const spacing: SpacingSizes = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

export const datePickerDebounceDelay = 3000;

export function findLast<A>(
  array: A[],
  predicate: (item: A, index: number, array: A[]) => boolean
) {
  let l = array.length;
  while (l--) {
    if (predicate(array[l], l, array)) return array[l];
  }
  return null;
}

export function diff(a, b) {
  return Math.abs(a - b);
}
export const COREMARKETS_URL: string = "https://coremarkets.co";
export const COREMARKETS_TERMS_OF_USE_URL: string =
  "https://coremarkets.co/terms-of-use";
export const PLATFORM_TERMS_OF_USER_URL: string =
  "https://coremarkets.co/platform-terms";
export const COREMARKETS_PRIVACY_POLICY_URL: string =
  "https://coremarkets.co/privacy-policy";
export const BROKER_HOTLINE = "+61-2-9135-4932";
export const BROKER_HOTLINE_DISPLAY = "+61 (2) 9135 4932";
export const PRODUCT_NAME = "CORE Markets";
export const WHITELIST_URLS = [
  "https://api.coremarkets.co",
  "https://api-uat.coremarkets.co",
];
export const HELP_URL = "https://docs.coremarkets.co";
export const DEFAULT_HOMEPAGE_URL = "/home";
export const PRODUCT_GLOSSARY_URL =
  "https://docs.coremarkets.co/carbon-products";

export const ATTRIBUTION_AEMO = {
  name: "AEMO",
  url: "https://www.aemo.com.au",
};

export const ATTRIBUTION_DIGITAL_ATLAS = {
  name: "Digital Atlas",
  url: "https://digital.atlas.gov.au/",
};

export type Region = "Qld" | "NSW" | "Vic" | "Tas" | "SA";
export type RegionFullName =
  | "Queensland"
  | "New South Wales"
  | "South Australia"
  | "Victoria"
  | "Tasmania";

export const contentHeaderForPriceTable: Region[] = ["Qld", "NSW", "Vic", "SA"];
export const carbonProducts: Exclude<
  (
    | adminMarketPriceTableDefinitions
    | approvedMarketPriceTableDefinitions
  )["name"],
  undefined
>[] = ["ACCU", "SMC", "NZU", "CER", "GS", "VCU"];

export const EnviroProducts: Exclude<
  (
    | adminMarketPriceTableDefinitions
    | approvedMarketPriceTableDefinitions
  )["name"],
  undefined
>[] = ["STC", "LGC", "ESC", "VEEC", "PRC", "IREC", "REGO", "REGO-BB"];

export const FEATURE_FLAG_DAILY_CURVES_CARBON = "benchmarks-carbon";

export const yearRange = (start: number, stop: number, step: number) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

export const centralAustraliaLocation: number[] = [
  -24.58460252143749, 134.7907170759688,
];
export const internationalLocation: number[] = [
  30.44867367928756, 15.110581440040985,
];
export const newZealandLocation: number[] = [
  -41.57436130598913, -185.97548150837036,
];

export const groupBy = <T>(
  xs: T[],
  getGroup: (x: T) => string
): { [key: string]: Array<T> } =>
  xs.reduce((rv, x) => {
    const key = getGroup(x);
    (rv[key] = rv[key] || []).push(x);
    return rv;
  }, {});

export const objectHasKeys = <T extends object>(
  obj: T,
  expectedKeys: string[]
): boolean => {
  return expectedKeys.every((key) => key in obj);
};

export const uniqueByKey = <A>(array: A[], key: string | number) => [
  ...new Map(array.map((x) => [x[key], x])).values(),
];

export const uniqueByMultipleKeys = <A extends object>(
  array: A[],
  keys: (keyof A)[]
) => [...new Map(array.map((x) => [keys.map((k) => x[k]).join(), x])).values()];

export const toCamelCase = (str: string): string => {
  if (!str) return str;

  if (/[ _-]/.test(str)) {
    const parts = str.split(/[_\-\s]+/).filter(Boolean);
    if (parts.length === 0) return str;

    const lowerParts = parts.map((p) => p.toLowerCase());
    const first = lowerParts[0];
    const rest = lowerParts
      .slice(1)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1));
    return [first, ...rest].join("");
  }

  // If string is all uppercase, convert to all lowercase
  if (str === str.toUpperCase()) {
    return str.toLowerCase();
  }

  return str.charAt(0).toLowerCase() + str.slice(1);
};

export const keysToCamelCase = (o) => {
  if (Array.isArray(o)) {
    return o.map((i) => {
      return keysToCamelCase(i);
    });
  } else if (o instanceof Object) {
    const n = {};

    Object.keys(o).forEach((k) => {
      n[toCamelCase(k)] = keysToCamelCase(o[k]);
    });

    return n;
  }

  return o;
};

export const toSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, (c) => {
    return "_" + c.toLowerCase();
  });
};

export const keysToSnake = (o) => {
  if (Array.isArray(o)) {
    return o.map((i) => {
      return keysToSnake(i);
    });
  } else if (o instanceof Object) {
    const n = {};

    Object.keys(o).forEach((k) => {
      n[toSnake(k)] = keysToSnake(o[k]);
    });

    return n;
  }

  return o;
};

export const removeWhiteSpace = (str: string) => {
  if (!str) return str;
  return str.replace(/^\s+/g, "");
};

export const capitalizeFirstLetter = (str: string): string =>
  str.charAt(0).toUpperCase() + str.substring(1);

// Subtype abbreviations stored as lowercase in the DB but displayed as ALL CAPS
const TECHNOLOGY_SUBTYPE_ABBREVIATIONS = new Set(["ccgt", "ocgt"]);

export const formatTechnologyTypeLabel = (label: string): string => {
  const lower = label.toLowerCase().trim();
  if (TECHNOLOGY_SUBTYPE_ABBREVIATIONS.has(lower)) {
    return label.toUpperCase();
  }
  return capitalizeFirstLetter(label);
};

export const formatTechnologyDisplayString = (display: string): string =>
  display.replace(
    // Bounded so an unclosed "(" cannot drive quadratic backtracking; technology
    // subtype labels are a handful of words.
    /\(([^)]{1,200})\)/g,
    (_, subtype) => `(${formatTechnologyTypeLabel(subtype.trim())})`
  );

export const toTitleCase = (str: string): string =>
  str
    .toLowerCase()
    .replace(/(^|[-\s])(\w)/g, (_, sep, char) => sep + char.toUpperCase());

// Minor words stay lower case in a title unless they are the first or last word.
const TITLE_CASE_MINOR_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "from",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
  "with",
]);

const toTitleCaseWord = (word: string, isFirstOrLast: boolean): string => {
  // Deliberate casing after the first character (API, AEMO, CORE, DB) is kept as is.
  if (/[A-Z]/.test(word.slice(1))) return word;
  if (!isFirstOrLast && TITLE_CASE_MINOR_WORDS.has(word.toLowerCase()))
    return word.toLowerCase();
  return capitalizeFirstLetter(word);
};

// Title cases a display label without flattening acronyms or promoting minor
// words, e.g. "News and alerts" -> "News and Alerts", "API tokens" -> "API Tokens".
export const toTitleCaseLabel = (label: string): string => {
  const words = label.split(" ");
  return words
    .map((word, index) =>
      toTitleCaseWord(word, index === 0 || index === words.length - 1)
    )
    .join(" ");
};

const toReplaceDoubleQuote = (str: string): string => {
  return str.replace(/"/g, '""');
};

export const escapeDoubleQuotes = (o) => {
  if (Array.isArray(o)) {
    if (Array.isArray(o[0])) {
      const d: string[][] = [];

      o.map((i) => {
        const c: string[] = [];

        i.map((j) => c.push(toReplaceDoubleQuote(j?.toString() ?? "")));
        return d.push(c);
      });

      return d;
    } else if (typeof o[0] === "string") {
      const d: string[] = [];

      o.map((i) => d.push(toReplaceDoubleQuote(i?.toString() ?? "")));

      return d;
    } else {
      const d: object[] = [];

      o.map((i) => {
        const n = {};
        Object.keys(i).forEach((k) => {
          n[k] = toReplaceDoubleQuote(i[k]?.toString() ?? "");
        });
        return d.push(n);
      });
      return d;
    }
  }

  return o;
};

export const currencyLongName = (currency: string) =>
  ({
    AUD: "Australian Dollars",
    USD: "American Dollars",
    NZD: "New Zealand Dollars",
    CAD: "Canadian Dollars",
    EUR: "Euros",
  }[currency]);

export const useOpenClose: (init: boolean) => {
  isOpen: boolean;
  closer: () => void;
  opener: () => void;
} = (init: boolean) => {
  const [isOpen, setIsOpen] = useState<boolean>(init);
  const opener = useCallback(() => setIsOpen(true), [setIsOpen]);
  const closer = useCallback(() => setIsOpen(false), [setIsOpen]);
  return { isOpen, opener, closer };
};

export const titleFontFamily = "Inter, sans-serif";
export const bodyFontFamily = "Inter, sans-serif";

export const useSearchParams = <P extends Record<string, string>>(): P => {
  const location = useLocation<P>();
  const searchParams = new URLSearchParams(location.search);
  let params = {} as P;
  for (const key of searchParams.keys()) {
    params[key as keyof P] = searchParams.get(key) as P[keyof P];
  }
  return params;
};
export const chartFont = "sans-serif";
export const startsWithWhiteSpace = (value: string): boolean =>
  // Everything after ^\S was inert: .* already spans the rest and
  // [a-zA-Z\s]* matches empty, so only the first character was ever tested.
  !/^\S/.test(value);

export const stringContainsOnlyWhiteSpace = (value: string): boolean =>
  /^\s*$/.test(value);

export const isValidHttpUrl = (str: string) => {
  const pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$", // fragment locator
    "i"
  );
  return pattern.test(str);
};

export const getArrayChanges = <T>(oldArray: T[] = [], newArray: T[] = []) => {
  const oldSet = new Set(oldArray);
  const newSet = new Set(newArray);
  const addedValues = Array.from(newSet).filter((value) => !oldSet.has(value));
  const deletedValues = Array.from(oldSet).filter(
    (value) => !newSet.has(value)
  );

  return {
    addedValues: addedValues.length ? addedValues : null,
    deletedValues: deletedValues.length ? deletedValues : null,
  };
};

export const formatPrice = (number?: number | null) =>
  number !== null && number !== undefined ? NumberFormat(number, 2) : "-";

export const useHashRouter = () => {
  const [hash, setHash] = useHash();
  const hashName = hash === "" ? undefined : hash.replace(/#/g, "");
  return {
    hashName,
    setHash,
  };
};

export const caseInsensitiveIncludes = (
  baseString: string,
  search: string
): boolean => {
  return baseString.toUpperCase().includes(search.toUpperCase());
};

export const emissionFormatRender = (emissionValue: number): string => {
  const formatter = Intl.NumberFormat("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
  if (emissionValue === null) return "0";
  return formatter.format(emissionValue);
};

export const findBestMagnitude = (value: number): MagnitudeTableEmission => {
  switch (true) {
    case value >= 1_000_000_000:
      return "Gt";
    case value >= 1_000_000:
      return "Mt";
    case value >= 1_000:
      return "kt";
    default:
      return "t";
  }
};

export const sumArrayValues = (arr: number[], n: number) => {
  return arr.slice(0, n + 1).reduce((a, c) => a + c, 0);
};

export const defaultStrToInt = (
  str: string | undefined | null,
  def: number
): number => {
  if (str === undefined || str === null) return def;
  const parsedStr = parseInt(str);
  if (isNaN(parsedStr)) return def;
  return parsedStr;
};

export const generateSequence = (i: number): number[] => {
  return Array.from(Array(i).keys());
};

export const usePageNumber = (initPageNumber = 1) => {
  const [pageNumber, setPageNumber] = useState<number>(initPageNumber);
  return {
    pageNumber,
    setPageNumber,
    getNextPage: () => setPageNumber((curPageNumber) => curPageNumber + 1),
    getPreviousPage: () => setPageNumber((curPageNumber) => curPageNumber - 1),
    setToFirstPage: () => setPageNumber(1),
  };
};

export const compareObjects = <T>(oldObj: T, newObj: T) => {
  if (typeof oldObj !== "object" || typeof newObj !== "object")
    throw new Error("Both parameters must be objects");

  const changedValues = {};
  for (const [key, value] of Object.entries(newObj))
    if (oldObj[key] !== value) changedValues[key] = value;
  return changedValues;
};

export const checkIsAValidJson = (jsonStr: string): boolean => {
  try {
    JSON.parse(jsonStr);
    return true;
  } catch {
    return false;
  }
};

export const debounce = (
  debounceRef: React.MutableRefObject<NodeJS.Timeout | null>,
  callback: Function,
  delay: number,
  maxTimeoutRef?: React.MutableRefObject<NodeJS.Timeout | null>,
  timeout?: number
) => {
  if (maxTimeoutRef && !maxTimeoutRef.current) {
    maxTimeoutRef.current = setTimeout(() => {
      callback();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      maxTimeoutRef.current = null;
    }, timeout ?? 5000);
  }

  if (debounceRef.current) {
    clearTimeout(debounceRef.current);
  }

  debounceRef.current = setTimeout(() => {
    callback();
    if (maxTimeoutRef?.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
  }, delay);
};

export const buildQuerySelect = (querySelect: Record<string, string>) =>
  Object.entries(querySelect)
    .map(([key, value]) => `${key}:${value}`)
    .join(",");

// PostgREST silently truncates unquoted list values at the first `,` or `)`.
export const toPostgrestInList = (values: (string | number)[]) =>
  `(${values
    .map((v) => `"${String(v).replace(/[\\"]/g, "\\$&")}"`)
    .join(",")})`;

export const buildQueryJSONBSelect = (
  subSelect: string,
  querySelect: Record<string, string>
) =>
  Object.entries(querySelect)
    .map(([key, value]) => `${key}:${subSelect}->${value}`)
    .join(",");

export const sortArrayData = <T>(
  data: T[],
  sortOrder: "asc" | "desc" = "asc",
  key: string
): T[] => {
  return data.sort((a, b) => {
    const aKey = a[key];
    const bKey = b[key];

    if (typeof aKey === typeof bKey) {
      if (aKey < bKey) return sortOrder === "asc" ? -1 : 1;
      if (aKey > bKey) return sortOrder === "asc" ? 1 : -1;
      return 0;
    }

    return typeof aKey === "string" ? -1 : 1;
  });
};

export const sortArrayDataNullFirst = <T>(
  data: T[],
  sortOrder: "asc" | "desc" = "asc",
  key: keyof T
): T[] => {
  return [...data].sort((a, b) => {
    const aKey = a[key];
    const bKey = b[key];

    if (aKey === null || aKey === undefined) return -1;
    if (bKey === null || bKey === undefined) return 1;

    if (aKey < bKey) return sortOrder === "asc" ? -1 : 1;
    if (aKey > bKey) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });
};

// Only plain data objects (`{}` literals) should be walked into. Class
// instances (Moment, Date, etc.) have their own internal properties — e.g. a
// Moment's private `_pf` bag contains `null` fields — which would otherwise
// make a populated value like a selected date falsely count as "empty".
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  (Object.getPrototypeOf(value) === Object.prototype ||
    Object.getPrototypeOf(value) === null);

export const hasEmptyValues = <T extends Record<string, any>>(
  obj?: T,
  exceptKeys?: (keyof T)[]
): boolean => {
  if (!obj) return true;
  if (Object.keys(obj).length === 0) return true;

  const keysToExclude = new Set(exceptKeys);

  return Object.entries(obj).some(([key, value]) => {
    if (keysToExclude.has(key as keyof T)) return false;

    return (
      value == null ||
      value === "" ||
      (Array.isArray(value) && !value.length) ||
      (isPlainObject(value) && hasEmptyValues(value as T, exceptKeys))
    );
  });
};

export const removeNullsFromObjectArrays = <T extends object>(
  obj: T
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [key, value.filter((item) => item !== null)];
      }
      return [key, value];
    })
  ) as Partial<T>;
};

export const pickSomeKeyObject = <T extends {}, K extends keyof T>(
  obj: T,
  ...keys: K[]
) =>
  Object.fromEntries(
    keys.filter((key) => key in obj).map((key) => [key, obj[key]])
  ) as Pick<T, K>;

export const validateIdParam = (id: string) =>
  id && id !== "0" && /^\d+$/.test(id) ? parseInt(id, 10) : null;

export const executeFunctionWhenIdle = (
  callback: () => void,
  timeout: number = 500
) => {
  if ("requestIdleCallback" in window) {
    const id = requestIdleCallback(() => {
      callback();
    });
    return () => cancelIdleCallback(id);
  } else {
    const timeoutId = setTimeout(() => {
      callback();
    }, timeout);
    return () => clearTimeout(timeoutId);
  }
};

const pendingOrderedIdleTasks: { order: number; run: () => void }[] = [];

const runNextOrderedIdleTask = () => {
  if (!pendingOrderedIdleTasks.length) return;
  let next = 0;
  for (let i = 1; i < pendingOrderedIdleTasks.length; i++) {
    if (
      pendingOrderedIdleTasks[i].order < pendingOrderedIdleTasks[next].order
    ) {
      next = i;
    }
  }
  pendingOrderedIdleTasks.splice(next, 1)[0].run();
};

/**
 * Idle callbacks run in request order, so independent chains paint in whatever
 * order their data arrived. Still requesting one callback per task, but running
 * the lowest `order` pending, keeps the throughput and paints top-down instead.
 */
const executeFunctionWhenIdleInOrder = (
  order: number,
  callback: () => void
) => {
  pendingOrderedIdleTasks.push({ order, run: callback });
  executeFunctionWhenIdle(runNextOrderedIdleTask);
};

/**
 * Without `renderOrder` each caller races every other caller for idle slots.
 * Pass one — lower paints first — to order callers that sit side by side on a
 * page; the numbers only have to be comparable across those callers.
 */
export const useProgressiveRendering = ({
  allKeys,
  batchSize = 1,
  renderOrder,
}: {
  allKeys: string[];
  batchSize?: number;
  renderOrder?: number;
}) => {
  const [renderedKeys, setRenderedKeys] = useState<string[]>([]);

  useDeepCompareEffect(() => {
    if (!allKeys.length) return;
    const renderBatch = () => {
      const renderNextKeys = () => {
        startTransition(() => {
          setRenderedKeys((prev) => {
            const updated = [
              ...prev,
              ...allKeys.slice(prev.length, prev.length + batchSize),
            ];

            if (updated.length < allKeys.length) {
              renderBatch();
            }

            return updated;
          });
        });
      };

      if (renderOrder === undefined) {
        executeFunctionWhenIdle(renderNextKeys);
      } else {
        executeFunctionWhenIdleInOrder(renderOrder, renderNextKeys);
      }
    };
    renderBatch();
  }, [allKeys]);

  return renderedKeys;
};

/**
 * DOMPurify config for XSS prevention.
 * Blocks: script, iframe, form, events, javascript: URLs
 * Allows: Rich text formatting (bold, lists, tables, links, images)
 */
const inputSanitizeConfig: Config = {
  // Block dangerous tags that can execute code or manipulate page
  FORBID_TAGS: [
    "script",
    "iframe",
    "frame",
    "frameset",
    "object",
    "embed",
    "applet",
    "form",
    "input",
    "button",
    "select",
    "textarea",
    "meta",
    "base",
    "link",
    "style", // CSS injection
  ],
  // Block dangerous attributes that can execute JavaScript
  FORBID_ATTR: [
    "onerror",
    "onload",
    "onclick",
    "onmouseover",
    "onmouseout",
    "onmouseenter",
    "onmouseleave",
    "onfocus",
    "onblur",
    "onchange",
    "onsubmit",
    "onkeydown",
    "onkeyup",
    "onkeypress",
  ],
  // Allow specific attributes needed for rich text
  ADD_ATTR: [
    "target",
    "rel", // For noopener noreferrer
    "alt",
    "title",
    "width",
    "height",
    "class",
    "id",
  ],
  // Allowed URI schemes (block javascript:, data: with scripts). Backticks disallowed to prevent obfuscation.
  ALLOWED_URI_REGEXP:
    /^(?:(?:(?:f|ht)tps?|mailto|tel|sms):|[^a-z`]|[a-z+.-]+(?:[^a-z+.:`-]|$))/i,
  // Keep structure intact
  FORCE_BODY: true,
  // Sanitize SVG (DOMPurify handles SVG-specific XSS)
  USE_PROFILES: { html: true, svg: true, svgFilters: true },
};

/**
 * Sanitize HTML for XSS prevention (no wrapper div).
 * Auto-adds rel="noopener noreferrer" to external links.
 */
export const sanitizeHtmlInputRaw = (
  html?: string,
  config?: Config
): string => {
  if (!html) return "";

  // Sanitize with permissive config - without wrapper div
  const sanitized = DOMPurify.sanitize(html, {
    ...inputSanitizeConfig,
    ...config,
  });

  // Post-processing: Add rel="noopener noreferrer" to all anchor tags
  // This prevents tab-nabbing attacks when target="_blank" is used
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = sanitized;

  const anchors = tempDiv.getElementsByTagName("a");
  for (let i = 0; i < anchors.length; i++) {
    const anchor = anchors[i];
    // If target="_blank" exists, ensure rel includes noopener noreferrer
    if (anchor.getAttribute("target") === "_blank") {
      const existingRel = anchor.getAttribute("rel") || "";
      const relParts = existingRel.split(" ").filter(Boolean);

      if (!relParts.includes("noopener")) {
        relParts.push("noopener");
      }
      if (!relParts.includes("noreferrer")) {
        relParts.push("noreferrer");
      }

      anchor.setAttribute("rel", relParts.join(" "));
    }
  }

  return tempDiv.innerHTML;
};

export const sanitizeHtmlInput = (html?: string, config?: Config): string => {
  // Reuse sanitizeHtmlInputRaw to avoid code duplication
  const sanitized = sanitizeHtmlInputRaw(html, config);
  if (!sanitized) return "";

  // Wrap in display-jodit-content div for consistent styling
  return `<div class="display-jodit-content">${sanitized}</div>`;
};

export const transformNumericSorting =
  <T extends Record<string, unknown>>(
    rawValueFn: (row: T) => number | string | null | undefined
  ): CORETableColumn<T>["sortingFn"] =>
  (rowA, rowB) => {
    const a = Number(rawValueFn(rowA.original as T)) || 0;
    const b = Number(rawValueFn(rowB.original as T)) || 0;
    return a - b;
  };
